import type { PublishedPortalSnapshotRecord } from './types.js';
import { ApiClientError, type ErrorEnvelope, type SuccessEnvelope } from './client.js';
import { loadRuntimeEnv } from '../app/runtimeEnv.js';

type ApiEnvelope<T> = SuccessEnvelope<T> | ErrorEnvelope;

function baseUrl(): string {
  const configured = loadRuntimeEnv().VITE_API_BASE_URL;
  return configured.endsWith('/') ? configured.slice(0, -1) : configured;
}

function buildUrl(path: string): string {
  return `${baseUrl()}${path.startsWith('/') ? path : `/${path}`}`;
}

async function parseEnvelope<T>(response: Response): Promise<ApiEnvelope<T>> {
  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
  if (!contentType.includes('application/json')) {
    const preview = (await response.text().catch(() => '')).trim().slice(0, 120);
    throw new ApiClientError(
      response.status || 500,
      'INVALID_API_RESPONSE',
      `Expected JSON API response but received "${contentType || 'unknown'}". Verify VITE_API_BASE_URL points to the API base.`,
      preview || undefined,
    );
  }

  try {
    return (await response.json()) as ApiEnvelope<T>;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Invalid JSON payload';
    throw new ApiClientError(
      response.status || 500,
      'INVALID_API_RESPONSE',
      `Failed to parse JSON API response: ${message}. Verify VITE_API_BASE_URL points to the API base.`,
    );
  }
}

async function requestPublic<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method,
    credentials: 'include',
    ...(body !== undefined
      ? {
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        }
      : {}),
  });

  const payload = await parseEnvelope<T>(response);
  if (!response.ok || payload.success === false) {
    if (payload.success === false) {
      throw new ApiClientError(response.status, payload.error.code, payload.error.message, payload.error.details);
    }
    throw new ApiClientError(response.status, 'INTERNAL_ERROR', 'Unexpected API response');
  }

  return payload.data;
}

export function unlockPublishedPortal(
  leagueId: string,
  payload: { password: string },
): Promise<{ unlocked: boolean; seasonName: string; publishedAt: string }> {
  return requestPublic('POST', `/public/leagues/${leagueId}/portal/session`, payload);
}

export function getPublishedPortalSnapshot(
  leagueId: string,
): Promise<{ snapshot: PublishedPortalSnapshotRecord }> {
  return requestPublic('GET', `/public/leagues/${leagueId}/portal`);
}

